/**
 * Phase 4+ — Audit Timeline Builder
 * 
 * Constructs an immutable, chronological audit timeline for a case.
 * Reads from authorization_activity_log and augments with computed events.
 * 
 * This timeline is:
 * - Chronological (oldest first)
 * - Immutable (read-only, append-only source)
 * - Exportable in PDF
 * - Viewable by compliance roles
 */

import type { AuditTimelineEvent, AuditTimelineEventType } from './types';

// ============================================================================
// DEMO TIMELINE BUILDER
// ============================================================================

/**
 * Build a comprehensive demo audit timeline showing all phases.
 */
export function buildDemoAuditTimeline(params: {
  caseId: string;
  caseNumber: string;
  authorizedCpts: string[];
  surgeonName?: string;
  hasAttestation: boolean;
  hasOpNote: boolean;
  hasAnalysis: boolean;
  hasConfirmations: boolean;
  hasExport: boolean;
  confirmedCpts?: string[];
}): AuditTimelineEvent[] {
  const events: AuditTimelineEvent[] = [];
  const surgeon = params.surgeonName || 'Dr. Michael Larkin, MD';
  const now = new Date();
  let offset = -3600000 * 4; // Start 4 hours ago

  const addEvent = (
    eventType: AuditTimelineEventType,
    description: string,
    actor: string,
    actorRole: string,
    opts?: Partial<AuditTimelineEvent>,
  ) => {
    offset += 120000 + Math.random() * 300000; // 2-7 min increments
    events.push({
      id: `demo-${events.length}`,
      timestamp: new Date(now.getTime() + offset).toISOString(),
      eventType,
      actor,
      actorRole,
      description,
      phiAccessed: opts?.phiAccessed ?? false,
      isOverride: opts?.isOverride ?? false,
      source: 'demo',
      ...opts,
    });
  };

  // Phase 1 — Authorization
  addEvent('authorization_created', `Authorization case ${params.caseNumber} created`, surgeon, 'surgeon', { phiAccessed: true });
  addEvent('subset_selected', `Subset template selected with ${params.authorizedCpts.length} CPTs: ${params.authorizedCpts.join(', ')}`, surgeon, 'surgeon');
  
  if (params.hasAttestation) {
    addEvent('surgeon_attestation', 'Surgeon attested: "I attest that the selected procedures represent clinically possible intra-operative findings for this case."', surgeon, 'surgeon');
    addEvent('authorization_approved', 'Authorization approved and submitted for payer review', 'LINK System', 'system');
  }

  // Phase 2.5 — Dictation (if op note exists)
  if (params.hasOpNote) {
    addEvent('dictation_started', 'Operative dictation session initiated', surgeon, 'surgeon');
    addEvent('dictation_completed', 'Dictation transcript captured and structured note parsed', surgeon, 'surgeon');
    addEvent('dictation_signed', 'Surgeon signed operative note. Note locked as immutable.', surgeon, 'surgeon');
    addEvent('op_note_uploaded', 'Operative note text stored for reconciliation', 'LINK System', 'system', { phiAccessed: true });
  }

  // Phase 3 — Reconciliation
  if (params.hasAnalysis) {
    addEvent('analysis_started', `Evidence analysis initiated for ${params.authorizedCpts.length} authorized CPTs`, 'LINK System', 'system');
    addEvent('proof_map_evaluated', `Proof map evaluated: documentation requirements matched against op note text`, 'LINK System', 'system');
    addEvent('analysis_completed', `Analysis complete. Evidence detected for CPTs with documentation status computed.`, 'LINK System', 'system');
  }

  // CPT confirmations
  if (params.hasConfirmations && params.confirmedCpts) {
    for (const cpt of params.confirmedCpts) {
      addEvent('cpt_confirmed_performed', `CPT ${cpt} confirmed as performed by surgeon`, surgeon, 'surgeon', { cptCode: cpt });
    }
    addEvent('modifier_applied', 'Modifier sequencing applied per payer rules', 'LINK System', 'system');
    addEvent('sequencing_completed', 'Final CPT sequencing completed with modifier validation', 'LINK System', 'system');
  }

  // Phase 4 — Export
  if (params.hasExport) {
    addEvent('export_gates_evaluated', 'All export gates evaluated. All checks passed.', 'LINK System', 'system');
    addEvent('export_generated', 'Epic-ready export package generated (Op Note + CPT String + PDF)', 'LINK Ops', 'link_ops');
  }

  return events;
}

// ============================================================================
// TIMELINE FROM DB ACTIVITY LOG
// ============================================================================

/**
 * Map activity log entries from the database to AuditTimelineEvent format.
 */
export function mapActivityLogToTimeline(
  activityLog: Array<{
    id: string;
    created_at: string;
    action: string;
    actor_id: string;
    actor_type: string;
    details: Record<string, unknown> | null;
  }>,
): AuditTimelineEvent[] {
  return activityLog.map(entry => {
    const eventType = mapActionToEventType(entry.action);
    return {
      id: entry.id,
      timestamp: entry.created_at,
      eventType,
      actor: entry.actor_id,
      actorRole: entry.actor_type,
      description: formatActionDescription(entry.action, entry.details),
      details: entry.details || undefined,
      cptCode: entry.details?.cpt_code as string | undefined,
      phiAccessed: entry.details?.phi_accessed === true,
      isOverride: entry.action.includes('override'),
      source: entry.actor_type === 'system' ? 'system' : 'user',
    };
  });
}

function mapActionToEventType(action: string): AuditTimelineEventType {
  const map: Record<string, AuditTimelineEventType> = {
    case_created: 'authorization_created',
    subset_selected: 'subset_selected',
    surgeon_attestation: 'surgeon_attestation',
    authorization_approved: 'authorization_approved',
    op_note_uploaded: 'op_note_uploaded',
    op_note_replaced: 'op_note_replaced',
    analysis_started: 'analysis_started',
    analysis_completed: 'analysis_completed',
    cpt_confirmed: 'cpt_confirmed_performed',
    cpt_not_performed: 'cpt_not_performed',
    modifier_applied: 'modifier_applied',
    modifier_overridden: 'modifier_overridden',
    export_generated: 'export_generated',
    export_failed: 'export_failed',
    override_applied: 'override_applied',
    phi_accessed: 'phi_accessed',
  };
  return map[action] || 'case_state_changed';
}

function formatActionDescription(action: string, details: Record<string, unknown> | null): string {
  if (details?.description && typeof details.description === 'string') {
    return details.description;
  }
  // Fallback to readable action
  return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
